import { useTranslation } from 'react-i18next';
import { useMe } from 'state/Administrators';
import { LinkItem } from 'types/Common';
import { sum } from 'utils/general';
import { useAdminDepositPendingActions } from 'store/admin-client-deposits';
import { useDiscountFeedbackItems } from 'store/feedback';
import {
  GlobalPermissionModules,
  LocalPermissionModules,
} from '../constants/Administrators';
import { ROUTES } from './routes';

export const useLinks = (): LinkItem[] => {
  const { t } = useTranslation();
  const { isFeatureEnabled, isMelpAdmin, hasViewPermission } = useMe();

  const { data: pendingActions } = useAdminDepositPendingActions({
    enabled: isMelpAdmin,
  });
  const { data: discountFeedbackItems } = useDiscountFeedbackItems(
    { status: ['pending'] },
    { enabled: isMelpAdmin },
  );

  return [
    {
      title: t('menu.benefits'),
      roles: ['hrAdmin'],
      module: GlobalPermissionModules.Benefits,
      links: [
        {
          title: t('menu.all_benefits'),
          to: ROUTES.benefits.list,
          roles: ['hrAdmin'],
          module: GlobalPermissionModules.Benefits,
          permission: ['benefits'],
        },
        {
          title: t('alternatives.alternatives'),
          to: ROUTES.alternatives.list,
          roles: ['hrAdmin'],
          module: GlobalPermissionModules.BenefitAlternatives,
          permission: ['benefits'],
        },
        ...(isFeatureEnabled('discounts')
          ? [
              {
                title: t('discounts.title'),
                to: ROUTES.discounts.list,
                roles: ['hrAdmin' as const],
                permission: ['discounts' as const],
              },
            ]
          : []),
      ],
    },
    {
      title: t('menu.marketplace'),
      roles: ['hrAdmin'],
      module: GlobalPermissionModules.Benefits,
      links: [
        isFeatureEnabled('marketplaceV2')
          ? {
              title: t('menu.marketplaceDeals'),
              to: ROUTES.brands.list,
              roles: ['hrAdmin' as const],
              permission: ['benefits' as const],
            }
          : {
              title: t('menu.marketplaceDeals'),
              to: ROUTES.shop.external.list,
              roles: ['hrAdmin' as const],
              permission: ['benefits' as const],
            },
        {
          title: t('shop.internal.title'),
          to: ROUTES.shop.internal.items.list,
          roles: ['hrAdmin'],
          permission: ['internalMerch'],
        },
        {
          title: t('menu.orders'),
          to: ROUTES.orders.list,
          roles: ['hrAdmin'],
          permission: ['internalMerchOrders', 'melpShopOrders'],
        },
        {
          title: t('wallet.wallet'),
          to: ROUTES.wallets.list,
          roles: ['hrAdmin'],
          permission: ['expenses'],
        },
        {
          title: t('expenses.title'),
          to: ROUTES.expenses.list,
          roles: ['hrAdmin'],
          permission: ['expenses'],
        },
        {
          title: t('expenses.filters.type.options.manual'),
          to: ROUTES.expenses.external.list,
          roles: ['hrAdmin'],
          permission: ['expenses'],
        },
        {
          title: t('claims.title'),
          to: ROUTES.claims.list,
          roles: ['hrAdmin'],
          permission: ['expenseCompensations'],
        },
      ],
    },
    {
      title: t('exportFileName.employees'),
      roles: ['hrAdmin'],
      links: [
        {
          title: t('exportFileName.employees'),
          to: ROUTES.employees.list,
          roles: ['hrAdmin'],
          module: LocalPermissionModules.Employees,
          permission: ['employees'],
        },
        {
          title: t('menu.groups'),
          to: ROUTES.employeeGroups.list,
          roles: ['hrAdmin'],
          module: GlobalPermissionModules.EmployeeGroups,
          permission: ['groups'],
        },
      ],
    },
    {
      title: t('menu.communication'),
      roles: ['hrAdmin'],
      links: [
        {
          title: t('permissions.newsModule'),
          to: ROUTES.news.list,
          roles: ['hrAdmin'],
          module: LocalPermissionModules.News,
          permission: ['news'],
        },
        {
          title: t('surveys.surveys'),
          to: ROUTES.surveys.survey.list,
          roles: ['hrAdmin'],
          permission: ['surveys'],
        },
        {
          title: t('recognition.recognition'),
          to: ROUTES.recognitions.list,
          roles: ['hrAdmin'],
          permission: ['employeeRecognition'],
        },
        {
          title: t('trustBox.trustBox'),
          to: ROUTES.trustBox.list,
          roles: ['hrAdmin'],
          permission: ['feedbackInbox'],
        },
      ],
    },
    ...(isFeatureEnabled('trainings')
      ? [
          {
            title: t('surveys.type.training.title'),
            to: ROUTES.surveys.training.list,
            roles: ['hrAdmin' as const],
            permission: ['trainings' as const],
          },
        ]
      : []),
    {
      title: t('sidebar.more'),
      roles: ['hrAdmin'],
      module: [
        GlobalPermissionModules.Administrators,
        GlobalPermissionModules.OrganisationStructure,
        LocalPermissionModules.Employees,
      ],
      links: [
        {
          title: t('automation.automation'),
          to: ROUTES.automations.list,
          roles: ['hrAdmin'],
          module: [],
          matchAllModules: true,
          permission: ['automations'],
        },
        {
          title: t('transmissionActs.title', { count: 2 }),
          to: ROUTES.transmissionActs.list,
          roles: ['hrAdmin'],
          permission: ['transmissionActs'],
        },
        ...(hasViewPermission(['employees'])
          ? [
              {
                title: t('sidebar.log'),
                to: ROUTES.logs.list,
                roles: ['hrAdmin' as const],
                module: LocalPermissionModules.History,
                permission: ['systemLog' as const],
              },
            ]
          : []),
        {
          title: t('common.settings'),
          to: '/more/company-settings',
          roles: ['hrAdmin'],
          module: [
            GlobalPermissionModules.Administrators,
            GlobalPermissionModules.OrganisationStructure,
          ],
          permission: [
            'companies',
            'generalSettings',
            'integrations',
            'administrators',
          ],
        },
      ],
    },
    {
      title: t('menu.clients'),
      roles: ['melpAdmin'],
      badge: pendingActions
        ? sum(pendingActions.clients, pendingActions.companies)
        : undefined,
      links: [
        {
          title: t('clients.clients.title'),
          to: ROUTES.admin.clients.list,
          roles: ['melpAdmin'],
        },
        {
          title: t('deposits.deposit_balance.title'),
          to: ROUTES.admin.deposits.balances.clients.list,
          roles: ['melpAdmin'],
          badge: pendingActions
            ? sum(pendingActions.clients, pendingActions.companies)
            : undefined,
        },
      ],
    },
    {
      title: t('menu.templates'),
      to: ROUTES.admin.benefitTemplates.list,
      roles: ['melpAdmin'],
    },
    {
      title: t('menu.integrations'),
      to: ROUTES.admin.integrations.list,
      roles: ['melpAdmin'],
    },
    {
      title: t('menu.marketplace'),
      to: ROUTES.admin.suppliers.list,
      roles: ['melpAdmin'],
      links: [
        {
          title: t('menu.suppliers'),
          to: ROUTES.admin.suppliers.list,
          roles: ['melpAdmin'],
        },
        {
          title: t('menu.items'),
          to: ROUTES.admin.shopItems.list,
          roles: ['melpAdmin'],
        },
        {
          title: t('menu.orders'),
          to: ROUTES.admin.orders.list,
          roles: ['melpAdmin'],
        },
        {
          title: t('shopUsers.title'),
          to: ROUTES.admin.shopUsers.list,
          roles: ['melpAdmin'],
        },
      ],
    },
    {
      title: t('discounts.title'),
      to: ROUTES.admin.discounts.list,
      roles: ['melpAdmin'],
      badge: discountFeedbackItems?.total,
      links: [
        {
          title: t('discounts.title'),
          to: ROUTES.admin.discounts.list,
          roles: ['melpAdmin'],
        },
        {
          title: t('alternatives.reports.title'),
          to: ROUTES.admin.discounts.reports,
          roles: ['melpAdmin'],
          badge: discountFeedbackItems?.total,
        },
      ],
    },
    {
      title: t('alternatives.reports.title'),
      to: ROUTES.admin.reports,
      roles: ['melpAdmin'],
    },
    {
      title: t('sidebar.administrators'),
      to: ROUTES.admin.admins.list,
      roles: ['melpAdmin'],
    },
  ] as const;
};
